import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, ArrowRight, Minus, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { toast } from "sonner";

export default function BoutiqueCheckoutModal({ product, onClose }) {
  const { lang } = useLang();
  const en = lang === "en";
  const [quantity, setQuantity] = useState(1);
  const [size, setSize] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  if (!product) return null;
  const isRepro = product.type === "reproduction";
  const total = (product.price * (isRepro ? 1 : quantity)) / 100;

  const submit = async (e) => {
    e.preventDefault();
    if (product.sizes && !size) {
      toast.error(en ? "Please choose a size." : "Merci de choisir une taille.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/boutique/checkout", {
        product_id: product.id,
        quantity: isRepro ? 1 : quantity,
        size: size || null,
        name, email,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
        data-testid="boutique-checkout-modal"
      >
        <div className="absolute inset-0 bg-fg/60 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.35, ease: [0.65, 0, 0.35, 1] }}
          className="relative bg-bg border border-fg/15 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 bg-bg/80 text-fg/60 hover:text-cinabre transition-colors"
            data-testid="boutique-modal-close"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>

          <div className="grid sm:grid-cols-5">
            <div className="sm:col-span-2 aspect-square sm:aspect-auto sm:min-h-full bg-fg/5 border-b sm:border-b-0 sm:border-r border-fg/15">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
            </div>
            <div className="sm:col-span-3 p-6 md:p-8">
              <div className="meta-label text-cinabre">§ {en ? "The Fil Shop" : "La Boutique du Fil"}</div>
              <h2 className="mt-2 font-serif font-light text-2xl md:text-3xl leading-tight tracking-tight">
                {product.name}{product.artist ? <span className="italic"> — {product.artist}</span> : ""}
              </h2>
              {isRepro && (
                <div className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/55">
                  {en ? <>Numbered edition /30 · signed on the back · {product.remaining} available</> : <>Tirage numéroté /30 · signé au dos · {product.remaining} disponible{product.remaining > 1 ? "s" : ""}</>}
                </div>
              )}
              {isRepro && (
                <p className="mt-3 text-sm text-fg/70 leading-relaxed">
                  {en ? "Your copy number (the next available) is assigned when your payment is confirmed." : "Votre numéro d'exemplaire (le prochain disponible) vous est attribué à la confirmation du paiement."}
                </p>
              )}

              <form onSubmit={submit} className="mt-6 space-y-4">
                {!isRepro && (
                  <div className="flex items-center gap-4">
                    <div className="meta-label text-fg/55">§ {en ? "Quantity" : "Quantité"}</div>
                    <div className="flex items-center border border-fg/20">
                      <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-3 py-2 hover:text-cinabre transition-colors" data-testid="boutique-qty-minus"><Minus size={14} /></button>
                      <span className="px-4 font-mono text-sm tabular-nums" data-testid="boutique-qty">{quantity}</span>
                      <button type="button" onClick={() => setQuantity(Math.min(5, quantity + 1))} className="px-3 py-2 hover:text-cinabre transition-colors" data-testid="boutique-qty-plus"><Plus size={14} /></button>
                    </div>
                  </div>
                )}

                {product.sizes && (
                  <div>
                    <div className="meta-label text-fg/55 mb-2">§ {en ? "Size" : "Taille"}</div>
                    <div className="flex gap-2 flex-wrap">
                      {product.sizes.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setSize(s)}
                          className={`px-4 py-2 border font-mono text-sm transition-colors ${size === s ? "border-cinabre bg-cinabre text-bg" : "border-fg/20 hover:border-cinabre"}`}
                          data-testid={`boutique-size-${s}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={en ? "Your name *" : "Votre nom *"}
                  className="w-full bg-bg border border-fg/20 px-4 py-3 text-[15px] focus:outline-none focus:border-cinabre transition-colors"
                  data-testid="boutique-buyer-name"
                />
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={en ? "Your email * (order confirmation)" : "Votre email * (confirmation de commande)"}
                  className="w-full bg-bg border border-fg/20 px-4 py-3 text-[15px] focus:outline-none focus:border-cinabre transition-colors"
                  data-testid="boutique-buyer-email"
                />
                <p className="text-xs text-fg/55 leading-relaxed">
                  {en ? "Your delivery address is requested at the secure payment step." : "L'adresse de livraison est demandée à l'étape de paiement sécurisé."}
                </p>

                <button
                  type="submit"
                  disabled={sending}
                  className="w-full inline-flex items-center justify-center gap-3 bg-cinabre text-bg px-8 py-4 font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-fg transition-colors disabled:opacity-50"
                  data-testid="boutique-checkout-submit"
                >
                  {sending ? (en ? "Redirecting…" : "Redirection…") : <>{en ? "Pay" : "Payer"} {total.toFixed(0)} € <ArrowRight size={14} /></>}
                </button>
                <div className="flex items-center justify-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50">
                  <Lock size={11} /> {en ? "Secure payment by Stripe" : "Paiement sécurisé par Stripe"}
                </div>
              </form>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
